// v2.97n — Filmistani Pro paywall modal.
// Shown when: free user hits Concierge daily cap, or clicks "Go Pro" anywhere.
// Includes: pricing tiers, founder live-counter, 7-day card-free trial CTA
// (only when eligible), Coming Soon feature teasers, coupon code support.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Calendar, Crown, Globe, Mail, Mic, Sparkles, Tag, Target, Trophy, Users, X,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useSubscription } from "../lib/SubscriptionContext";

const ICONS = {
  mic: Mic,
  mail: Mail,
  target: Target,
  users: Users,
  trophy: Trophy,
  calendar: Calendar,
  globe: Globe,
};

// Razorpay Checkout.js loader — same pattern as Support page.
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PaywallModal() {
  const { user } = useAuth();
  const { paywallOpen, closePaywall, trialOffer, founderSlotsRemaining, usageToday, refresh } =
    useSubscription();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(false);
  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [couponPlan, setCouponPlan] = useState(null); // plan code coupon is being applied for
  const [couponStatus, setCouponStatus] = useState(null); // null | "validating" | "valid" | "error"
  const [couponDetails, setCouponDetails] = useState(null); // { code, message }
  const [couponError, setCouponError] = useState("");

  useEffect(() => {
    if (!paywallOpen) return;
    api.get("/subscription/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, [paywallOpen]);

  // Reset coupon state when modal closes
  useEffect(() => {
    if (!paywallOpen) {
      setCouponInput("");
      setCouponPlan(null);
      setCouponStatus(null);
      setCouponDetails(null);
      setCouponError("");
    }
  }, [paywallOpen]);

  if (!paywallOpen) return null;

  const validateCoupon = async (planCode) => {
    const code = couponInput.trim();
    if (!code) return;
    if (!user) { toast.error("Sign in to use a coupon"); return; }
    setCouponStatus("validating");
    setCouponPlan(planCode);
    setCouponError("");
    try {
      const r = await api.post("/subscription/coupon/validate", { code, plan_code: planCode });
      setCouponStatus("valid");
      setCouponDetails({ code: r.data.code, message: r.data.message });
    } catch (e) {
      setCouponStatus("error");
      setCouponError(e?.response?.data?.detail || "Invalid coupon code");
      setCouponDetails(null);
    }
  };

  const clearCoupon = () => {
    setCouponInput("");
    setCouponPlan(null);
    setCouponStatus(null);
    setCouponDetails(null);
    setCouponError("");
  };

  const activateTrial = async () => {
    if (!user) {
      toast.error("Sign in with Google to activate your free trial");
      return;
    }
    setBusy(true);
    try {
      await api.post("/subscription/trial/activate");
      await refresh();
      toast.success("🎁 7-day Pro trial activated. Enjoy!");
      closePaywall();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate trial");
    } finally {
      setBusy(false);
    }
  };

  const goPro = async (planCode) => {
    if (!user) {
      toast.error("Sign in with Google to subscribe");
      return;
    }
    setBusy(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error("Razorpay failed to load. Try again.");
        setBusy(false);
        return;
      }
      // Apply coupon only if it was validated for this exact plan
      const activeCoupon =
        couponStatus === "valid" && couponPlan === planCode && couponDetails?.code
          ? couponDetails.code
          : null;
      const orderRes = await api.post("/subscription/order", {
        plan_code: planCode,
        ...(activeCoupon ? { coupon_code: activeCoupon } : {}),
      });
      api
        .post("/subscription/paywall-event", { kind: "upgrade_click" })
        .catch(() => {});
      const isSubscription = orderRes.data.kind === "subscription";
      // v2.97p — Razorpay Checkout takes EITHER `order_id` (one-time) OR
      // `subscription_id` (recurring with UPI AutoPay). The server tells us
      // which based on plan_code.
      const rzpOpts = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: "Filmistani Pro",
        description: orderRes.data.plan?.label || "Filmistani Pro",
        theme: { color: "#facc15" },
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        handler: async (rp) => {
          try {
            await api.post("/subscription/verify", {
              plan_code: planCode,
              razorpay_order_id: rp.razorpay_order_id,
              razorpay_subscription_id: rp.razorpay_subscription_id,
              razorpay_payment_id: rp.razorpay_payment_id,
              razorpay_signature: rp.razorpay_signature,
              ...(activeCoupon ? { coupon_code: activeCoupon } : {}),
            });
            await refresh();
            toast.success("💛 Welcome to Filmistani Pro! Enjoy unlimited Filmi.");
            closePaywall();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          } finally {
            setBusy(false);
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      };
      if (isSubscription) {
        rzpOpts.subscription_id = orderRes.data.subscription_id;
      } else {
        rzpOpts.order_id = orderRes.data.order_id;
      }
      const rzp = new window.Razorpay(rzpOpts);
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const founderRemain = plans?.founder_slots_remaining ?? founderSlotsRemaining;
  const founderClaimed = (plans?.founder_slots_cap ?? 200) - founderRemain;
  const comingSoon = plans?.coming_soon_features || [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={closePaywall}
      data-testid="paywall-backdrop"
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-amber-400/30 bg-neutral-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="paywall-modal"
      >
        {/* Close */}
        <button
          onClick={closePaywall}
          className="absolute top-4 right-4 text-neutral-400 hover:text-white transition"
          data-testid="paywall-close"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        <div className="p-8 sm:p-10">
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="text-amber-400" size={26} />
            <h2 className="text-2xl sm:text-3xl font-bold text-white">
              {usageToday?.remaining === 0
                ? "You've reached your daily Filmi limit"
                : "Unlock Filmistani Pro"}
            </h2>
          </div>
          <p className="text-neutral-400 mb-6">
            {usageToday?.remaining === 0
              ? `${usageToday.count}/${usageToday.limit} queries used today · resets at midnight IST`
              : "Unlimited curated picks + early access to upcoming features"}
          </p>

          {/* Trial offer — only when eligible */}
          {trialOffer?.eligible && (
            <div
              className="mb-6 rounded-xl border border-amber-400/40 bg-amber-400/5 p-5"
              data-testid="paywall-trial-offer"
            >
              <div className="flex items-start gap-3">
                <div className="text-2xl">🎁</div>
                <div className="flex-1">
                  <div className="font-semibold text-amber-300 mb-1">
                    Try Pro free for {trialOffer.trial_length_days || 7} days
                  </div>
                  <div className="text-sm text-neutral-400 mb-3">
                    No payment · No auto-charge · Cancel anytime
                  </div>
                  <button
                    onClick={activateTrial}
                    disabled={busy}
                    className="px-5 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-semibold transition disabled:opacity-50"
                    data-testid="paywall-start-trial"
                  >
                    {busy ? "Activating…" : "Start 7-Day Trial →"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* What you get */}
          <div className="mb-6">
            <div className="text-sm font-semibold text-neutral-300 mb-3">
              What Pro unlocks today
            </div>
            <ul className="space-y-2 text-sm text-neutral-300">
              <li>✅ Unlimited Concierge queries</li>
              <li>✅ Full rails (50+ titles each)</li>
              <li>✅ Top 100 lists · unlimited watchlist</li>
              <li>✅ Gold badge on profile</li>
            </ul>
          </div>

          {/* Coming Soon teaser */}
          {comingSoon.length > 0 && (
            <div className="mb-6">
              <div className="text-sm font-semibold text-neutral-300 mb-3 flex items-center gap-2">
                <span>🔒 Coming soon for Pro</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {comingSoon.map((f) => {
                  const Icon = ICONS[f.icon] || Sparkles;
                  return (
                    <div
                      key={f.label}
                      className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900/60 px-3 py-2 text-xs text-neutral-300"
                      data-testid={`coming-soon-${f.icon}`}
                    >
                      <Icon size={14} className="text-amber-400 shrink-0" />
                      <span className="truncate">{f.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pricing */}
          <div className="border-t border-neutral-800 pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {/* Monthly */}
              <button
                onClick={() => goPro("pro_monthly")}
                disabled={busy}
                className="rounded-xl border border-neutral-700 bg-neutral-900 hover:border-amber-400/60 hover:bg-neutral-900/80 transition p-4 text-left disabled:opacity-50"
                data-testid="paywall-plan-monthly"
              >
                <div className="text-xs text-neutral-400 mb-1">Monthly</div>
                <div className="text-xl font-bold text-white">₹49</div>
                <div className="text-xs text-neutral-500">per month</div>
              </button>

              {/* Annual */}
              <button
                onClick={() => goPro("pro_annual")}
                disabled={busy}
                className="rounded-xl border border-amber-400/40 bg-amber-400/5 hover:border-amber-400 transition p-4 text-left relative disabled:opacity-50"
                data-testid="paywall-plan-annual"
              >
                <div className="absolute -top-2 right-3 bg-amber-400 text-black text-[10px] font-bold px-2 py-0.5 rounded-full">
                  SAVE 33%
                </div>
                <div className="text-xs text-amber-400 mb-1">Annual · Best value</div>
                <div className="text-xl font-bold text-white">₹399</div>
                <div className="text-xs text-neutral-500">per year (₹33/mo)</div>
              </button>

              {/* Founder */}
              <button
                onClick={() => goPro("founder")}
                disabled={busy || founderRemain <= 0}
                className="rounded-xl border border-neutral-700 bg-gradient-to-br from-amber-500/10 to-orange-600/10 hover:border-amber-400 transition p-4 text-left relative disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="paywall-plan-founder"
              >
                <div className="flex items-center gap-1 mb-1">
                  <Crown size={12} className="text-amber-400" />
                  <div className="text-xs text-amber-400">Founder · Lifetime</div>
                </div>
                <div className="text-xl font-bold text-white">₹999</div>
                <div className="text-xs text-neutral-500">
                  {founderRemain > 0
                    ? `${founderClaimed} of 200 claimed`
                    : "Sold out"}
                </div>
              </button>
            </div>

            {/* Coupon code field — only for Monthly and Annual */}
            <div className="mb-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Tag size={13} className="text-neutral-400" />
                <span className="text-xs text-neutral-400 font-medium">Have a coupon code?</span>
                {couponStatus === "valid" && (
                  <span className="ml-auto text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full font-semibold">
                    Applied
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value.toUpperCase());
                    if (couponStatus !== null) clearCoupon();
                    setCouponInput(e.target.value.toUpperCase());
                  }}
                  placeholder="COUPON CODE"
                  disabled={busy || couponStatus === "valid"}
                  className={`flex-1 bg-neutral-900 border rounded-lg px-3 py-2 text-sm font-mono uppercase placeholder-neutral-600 outline-none transition ${
                    couponStatus === "valid"
                      ? "border-emerald-500/40 text-emerald-300"
                      : couponStatus === "error"
                      ? "border-rose-500/40 text-white"
                      : "border-white/10 text-white focus:border-amber-400/40"
                  } disabled:opacity-50`}
                  data-testid="paywall-coupon-input"
                />
                {couponStatus === "valid" ? (
                  <button
                    onClick={clearCoupon}
                    className="px-3 py-2 rounded-lg border border-white/10 text-neutral-400 hover:text-white text-xs transition"
                    data-testid="paywall-coupon-clear"
                  >
                    Clear
                  </button>
                ) : (
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => validateCoupon("pro_monthly")}
                      disabled={busy || !couponInput.trim() || couponStatus === "validating"}
                      className="px-3 py-2 rounded-lg border border-blue-500/30 text-blue-300 hover:border-blue-400 text-xs font-medium transition disabled:opacity-40"
                      data-testid="paywall-coupon-apply-monthly"
                      title="Apply to Monthly"
                    >
                      {couponStatus === "validating" && couponPlan === "pro_monthly" ? "…" : "Monthly"}
                    </button>
                    <button
                      onClick={() => validateCoupon("pro_annual")}
                      disabled={busy || !couponInput.trim() || couponStatus === "validating"}
                      className="px-3 py-2 rounded-lg border border-amber-500/30 text-amber-300 hover:border-amber-400 text-xs font-medium transition disabled:opacity-40"
                      data-testid="paywall-coupon-apply-annual"
                      title="Apply to Annual"
                    >
                      {couponStatus === "validating" && couponPlan === "pro_annual" ? "…" : "Annual"}
                    </button>
                  </div>
                )}
              </div>
              {couponStatus === "valid" && couponDetails && (
                <p className="text-xs text-emerald-400 mt-1.5 flex items-center gap-1" data-testid="paywall-coupon-success">
                  <span>✓</span>
                  {couponDetails.message} — Click {couponPlan === "pro_monthly" ? "Monthly" : "Annual"} to checkout
                </p>
              )}
              {couponStatus === "error" && (
                <p className="text-xs text-rose-400 mt-1.5" data-testid="paywall-coupon-error">{couponError}</p>
              )}
            </div>

            <div className="text-[11px] text-neutral-500 text-center">
              Powered by Razorpay · UPI, Cards, Wallets · 7-day refund guarantee
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
